import { useEffect, useMemo, useState } from "react";
import type { Tile, TileFrame } from "@/api/queries";
import type { Link } from "@/artifacts/links";
import { LinkedScope } from "@/components/artifacts/LinkedScope";
import { cn } from "@/lib/cn";
import { useGridScope } from "./gridScope";
import { ROWS, SPAN_COLUMNS, SPAN_ROWS } from "./gridFrames";
import { TileCard } from "./TileCard";

/**
 * The tile grid (PRD FR-7.2).
 *
 * **Layout is data, and drag is one way of editing it.** The order and the
 * spans live on the server; this component holds the arrangement only while a
 * drag is in flight and then persists the whole grid in one call — which is
 * what a single drag interaction actually produces (a move, and sometimes a
 * resize).
 *
 * It is reorderable **without a mouse**. Every tile's drag affordance is a real
 * button: focus it and the arrow keys move the tile through the order, which is
 * the same operation the pointer performs. A grid whose only reordering
 * gesture is a drag is a grid half the people who read reports cannot
 * rearrange.
 *
 * Four columns at desktop width, two at tablet, one below that — a tile's
 * `col_span` is a claim on a four-column grid, so narrower grids clamp it
 * rather than overflow (PRD NFR-5; dashboards are best-effort on phones, but a
 * report is something people read on one).
 */

export function TileGrid({
  tiles,
  links = [],
  noun = "report",
  showConnections,
  onLayout,
  onEditSql,
  onViewSql,
  saving,
}: {
  tiles: Tile[];
  /** The grid's links (LINK-7): a selection in one tile reaches the others. */
  links?: Link[];
  /** What the filter bar names: "Filter this report", "… dashboard". */
  noun?: "report" | "dashboard";
  showConnections: boolean;
  onLayout: (frames: TileFrame[]) => void;
  onEditSql: (tile: Tile) => void;
  onViewSql: (tile: Tile) => void;
  saving: boolean;
}) {
  const [order, setOrder] = useState<string[]>(() => tiles.map((tile) => tile.id));
  const [dragging, setDragging] = useState<string | null>(null);

  // The server is the source of truth; local order only survives while it
  // matches the set of tiles the server sent.
  useEffect(() => {
    setOrder((current) => {
      const ids = tiles.map((tile) => tile.id);
      const same =
        current.length === ids.length && current.every((id) => ids.includes(id));
      return same ? current : ids;
    });
  }, [tiles]);

  const byId = useMemo(() => new Map(tiles.map((tile) => [tile.id, tile])), [tiles]);
  const scope = useGridScope(tiles, { canFetch: true });
  const ordered = order.map((id) => byId.get(id)).filter((tile): tile is Tile => Boolean(tile));

  function commit(next: string[]) {
    setOrder(next);
    onLayout(
      next.map((id) => {
        const tile = byId.get(id);
        return { id, col_span: tile?.col_span ?? 2, row_span: tile?.row_span ?? 2 };
      }),
    );
  }

  function move(id: string, delta: number) {
    const from = order.indexOf(id);
    const to = Math.max(0, Math.min(order.length - 1, from + delta));
    if (from < 0 || from === to) return;
    const next = [...order];
    next.splice(to, 0, ...next.splice(from, 1));
    commit(next);
  }

  function dropOn(targetId: string) {
    if (!dragging || dragging === targetId) return;
    const next = order.filter((id) => id !== dragging);
    next.splice(order.indexOf(targetId), 0, dragging);
    setDragging(null);
    commit(next);
  }

  return (
    <LinkedScope
      links={links}
      view={scope.view}
      drill={scope.drill}
      zoom={scope.zoom}
      label={`Filter this ${noun}`}
      className="flex flex-col gap-4"
    >
      <div
        className={cn(
          "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4",
          ROWS,
          saving && "opacity-95",
        )}
      >
        {ordered.map((tile, index) => (
          <div
            key={tile.id}
            onDragOver={(event) => {
              if (dragging) event.preventDefault();
            }}
            onDrop={(event) => {
              event.preventDefault();
              dropOn(tile.id);
            }}
            className={cn(
              "min-w-0",
              // A col-span is a claim on the four-column grid; narrower grids
              // clamp it so nothing ever overflows sideways.
              tile.col_span >= 3 ? "sm:col-span-2" : "",
              SPAN_COLUMNS[tile.col_span] ?? "lg:col-span-2",
              SPAN_ROWS[tile.row_span] ?? "sm:row-span-2",
              dragging && dragging !== tile.id ? "rounded-container ring-border ring-1" : "",
            )}
          >
            <TileCard
              tile={tile}
              linkIndex={scope.indexOf(tile.id)}
              showConnection={showConnections}
              position={index + 1}
              total={ordered.length}
              dragging={dragging === tile.id}
              onGrab={(event) => {
                setDragging(tile.id);
                event.dataTransfer.effectAllowed = "move";
                // Firefox refuses to start a drag without payload.
                event.dataTransfer.setData("text/plain", tile.id);
              }}
              onMove={(delta) => move(tile.id, delta)}
              onEditSql={() => onEditSql(tile)}
              onViewSql={() => onViewSql(tile)}
            />
          </div>
        ))}
      </div>
    </LinkedScope>
  );
}
